/* Sounds nach Pfad: Schlüssel, Upload-Ziel auf dem Server und Auswahl der Sounds einer Karte (ohne DOM, node-testbar).
 * Der Schlüssel ist der Pfad relativ zum sound-Ordner des Spiels, wie ihn ambient_generic im Feld message nennt
 * („ambience/techamb1.wav“). Auf dem Server liegen die Dateien unter sounds/<Schlüssel>. */

const AUDIO = /\.(wav|mp3|ogg)$/i;

/** Pfad einer Sounddatei → Schlüssel: klein geschrieben, „/“ statt „\“, ohne führendes „./“, „/“ oder „sound/“ */
export function soundKey(v: unknown): string {
  let k = String(v ?? '').trim().replace(/\\/g, '/').toLowerCase();
  while (k.startsWith('./') || k.startsWith('/')) k = k.replace(/^\.?\//, '');
  return k.replace(/^sound\//, '');
}

export const soundBase = (key: string): string => key.replace(/^.*\//, '');

/** Zielpfad auf dem Server für eine Sounddatei. rel = relativer Pfad beim Hochladen eines Ordners („valve/sound/ambience/x.wav“,
 * „sound/ambience/x.wav“ oder „ambience/x.wav“); nach dem ersten Ordner „sound“ beginnt der Schlüssel, sonst gilt der ganze Pfad.
 * Ohne Ordner (einzelne Datei) bleibt es beim Namen: sounds/<name>. */
export function soundUploadPath(fileName: string, rel?: string): string {
  const name = soundKey(fileName.replace(/^.*[\\/]/, ''));
  const parts = soundKey(rel || '').split('/').filter(Boolean);
  if (parts.length < 2) return 'sounds/' + name;
  const i = parts.indexOf('sound');
  const inner = i >= 0 ? parts.slice(i + 1) : parts;
  return 'sounds/' + (inner.length ? inner.join('/') : name);
}

export const isSoundFile = (name: string): boolean => AUDIO.test(name);

/** Sound zu einem Schlüssel: erst der volle Pfad, sonst ein Sound mit gleichem Dateinamen (lokal geladene Dateien kennen ihren Ordner nicht) */
export function lookupSound<T>(sounds: Map<string, T>, key: string): T | undefined {
  const exact = sounds.get(key);
  if (exact !== undefined) return exact;
  const base = soundBase(key);
  for (const [k, v] of sounds) if (soundBase(k) === base) return v;
  return undefined;
}

export interface SoundAsset { path: string }
export interface MapSoundPlan {
  /** zu ladende Server-Dateien: Asset-Pfad und Schlüssel, unter dem sie im Editor abgelegt werden */
  load: { path: string; key: string }[];
  /** schon geladen */
  have: string[];
  /** auf dem Server nicht vorhanden */
  missing: string[];
}

/** Welche Sounds einer Karte vom Server geladen werden müssen. wanted: Schlüssel aus den ambient_generic der Karte;
 * loaded: Schlüssel der geladenen Sounds; assets: Asset-Liste des Servers (alle Pfade, nur sounds/ zählt). */
export function planMapSounds(wanted: string[], assets: SoundAsset[], loaded: string[]): MapSoundPlan {
  const plan: MapSoundPlan = { load: [], have: [], missing: [] };
  const have = new Map<string, string>(loaded.map((k) => [k, k]));
  const server = assets.filter((a) => a.path.startsWith('sounds/') && AUDIO.test(a.path)).map((a) => ({ path: a.path, key: a.path.slice(7) }))
    .sort((a, b) => (a.key < b.key ? -1 : 1));
  const seen = new Set<string>();
  for (const key of wanted) {
    if (!key || seen.has(key) || !AUDIO.test(key)) continue;   // Sätze („!HG_ALERT“) und Fremdes sind keine Dateien
    seen.add(key);
    if (lookupSound(have, key) !== undefined) { plan.have.push(key); continue; }
    const hit = server.find((s) => s.key === key) || server.find((s) => soundBase(s.key) === soundBase(key));
    if (hit) { if (!plan.load.some((l) => l.key === hit.key)) plan.load.push(hit); } else plan.missing.push(key);
  }
  return plan;
}
